import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Package, TrendUp, TrendDown, FloppyDisk, DownloadSimple, MagnifyingGlass, ChartBar, X, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import NumberInput from "@/components/ui/NumberInput";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtKg = (n) => `${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })} kg`;

// ─── Modal de desglose de precio prom/kg ──────────────────────────────────
function DesglosePrecioModal({ producto, fechaDesde, fechaHasta, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBucket, setExpandedBucket] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const params = { fecha_desde: fechaDesde, fecha_hasta: fechaHasta };
        if (producto.producto_id) params.producto_id = producto.producto_id;
        else if (producto.nombre) params.nombre = producto.nombre;
        const { data } = await api.get("/dashboard/productos-vendidos/desglose-precio", { params });
        setData(data);
      } catch (_) { setData(null); }
      finally { setLoading(false); }
    })();
  }, [producto, fechaDesde, fechaHasta]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="desglose-precio-modal">
      <div className="bg-white rounded-sm border border-neutral-300 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between bg-neutral-50">
          <div>
            <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
              <ChartBar size={16} weight="bold" className="text-[#6B1F24]" />
              Desglose de precio prom/kg — {producto.nombre}
            </div>
            <div className="text-[11px] text-neutral-500 font-mono mt-0.5">
              Kilos vendidos por bucket de precio · NC neteadas al mismo precio del original
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800" data-testid="desglose-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {loading && <div className="p-10 text-center text-neutral-500">Cargando…</div>}
        {!loading && data && (
          <>
            <div className="px-5 py-3 border-b border-neutral-100 grid grid-cols-4 gap-4 bg-white">
              <Metric label="Kg brutos (venta)" value={fmtKg(data.total_kg_bruto)} tone="emerald" />
              <Metric label="Kg NC (devueltos)" value={fmtKg(data.total_kg_nc)} tone="rose" negative />
              <Metric label="Kg NETO facturado" value={fmtKg(data.total_kg_neto)} tone="neutral" bold />
              <Metric label="Precio prom ponderado" value={fmtMoney(data.precio_prom_ponderado)} tone="sky" bold />
            </div>
            <div className="overflow-y-auto flex-1">
              {data.buckets.length === 0 ? (
                <div className="p-10 text-center text-neutral-400">Sin datos de venta en el período.</div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left w-8"></th>
                      <th className="px-4 py-2 text-right">Precio/kg</th>
                      <th className="px-4 py-2 text-right">Kg vendidos</th>
                      <th className="px-4 py-2 text-right">Kg en NC</th>
                      <th className="px-4 py-2 text-right">Kg NETO</th>
                      <th className="px-4 py-2 text-right">Facturado NETO</th>
                      <th className="px-4 py-2 text-right">% mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.buckets.map((b, i) => {
                      const isExp = expandedBucket === b.precio;
                      return (
                        <>
                          <tr key={b.precio} className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                              onClick={() => setExpandedBucket(isExp ? null : b.precio)}
                              data-testid={`desglose-bucket-${i}`}>
                            <td className="px-4 py-1.5 text-neutral-400 text-center">{isExp ? "▾" : "▸"}</td>
                            <td className="px-4 py-1.5 text-right font-mono font-bold">{fmtMoney(b.precio)}</td>
                            <td className="px-4 py-1.5 text-right font-mono text-emerald-700">+{fmtKg(b.kg_bruto).replace(" kg","")}</td>
                            <td className="px-4 py-1.5 text-right font-mono text-rose-700">
                              {b.kg_nc > 0 ? `-${fmtKg(b.kg_nc).replace(" kg","")}` : "—"}
                            </td>
                            <td className="px-4 py-1.5 text-right font-mono font-bold">{fmtKg(b.kg_neto).replace(" kg","")}</td>
                            <td className="px-4 py-1.5 text-right font-mono">{fmtMoney(b.facturado_neto)}</td>
                            <td className="px-4 py-1.5 text-right">
                              <div className="relative w-full bg-neutral-100 rounded-full h-3 overflow-hidden">
                                <div className="absolute inset-y-0 left-0 bg-[#6B1F24] rounded-full"
                                     style={{ width: `${Math.min(100, Math.max(0, b.porcentaje_neto))}%` }}></div>
                                <div className="relative text-[9px] font-mono font-bold text-neutral-800 text-center leading-3">
                                  {b.porcentaje_neto.toFixed(1)}%
                                </div>
                              </div>
                            </td>
                          </tr>
                          {isExp && b.ejemplos && b.ejemplos.length > 0 && (
                            <tr className="bg-neutral-50/60">
                              <td colSpan={7} className="px-4 py-2">
                                <div className="text-[10px] uppercase font-mono text-neutral-500 mb-1">
                                  Últimos comprobantes a {fmtMoney(b.precio)}:
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px] font-mono">
                                  {b.ejemplos.map((e, j) => (
                                    <div key={j} className={`flex items-center gap-2 ${e.es_nc ? "text-rose-700" : "text-neutral-700"}`}>
                                      <span className="font-bold">{e.tipo_comprobante} {e.comprobante}</span>
                                      <span className="text-neutral-400">{(e.fecha || "").slice(0,10)}</span>
                                      <span className="ml-auto">{e.es_nc ? "-" : "+"}{Math.abs(e.kilos)} kg</span>
                                      <span className="text-neutral-500 truncate max-w-[140px]" title={e.cliente}>{e.cliente || ""}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, tone = "neutral", bold, negative }) {
  const cls = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    sky: "text-sky-700",
    neutral: "text-neutral-800",
  }[tone] || "text-neutral-800";
  return (
    <div>
      <div className="text-[9px] uppercase font-mono text-neutral-500">{label}</div>
      <div className={`text-lg font-heading ${bold ? "font-bold" : "font-semibold"} ${cls}`}>
        {negative ? "−" : ""}{value}
      </div>
    </div>
  );
}

export default function ProductosVendidosCard({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [nuevoCosto, setNuevoCosto] = useState({}); // {producto_id: "1234.56"}
  const [q, setQ] = useState("");
  const [desglose, setDesglose] = useState(null); // producto para el modal
  const [soloAlertas, setSoloAlertas] = useState(false);
  const [alertasDetalle, setAlertasDetalle] = useState(null); // producto para modal alertas
  const [visibleCount, setVisibleCount] = useState(10);

  const cargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/productos-vendidos", {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
      });
      setData(data);
      setNuevoCosto({});
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);
  useEffect(() => { cargar(); }, [cargar]);

  const productos = data?.productos || [];
  const totalAlertas = productos.reduce((s, p) => s + (p.cant_alertas || 0), 0);
  const productosConAlerta = productos.filter(p => (p.cant_alertas || 0) > 0).length;
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let arr = productos;
    if (soloAlertas) arr = arr.filter(p => (p.cant_alertas || 0) > 0);
    if (!s) return arr;
    return arr.filter(p => (p.nombre || "").toLowerCase().includes(s) || (p.codigo || "").toLowerCase().includes(s));
  }, [productos, q, soloAlertas]);

  // Al cambiar filtros o dataset, resetear el contador visible a 10.
  useEffect(() => { setVisibleCount(10); }, [q, soloAlertas, productos.length]);

  const visibleRows = useMemo(
    () => (visibleCount >= filtered.length ? filtered : filtered.slice(0, visibleCount)),
    [filtered, visibleCount]
  );

  const calcDiff = (p) => {
    const nc = parseFloat(nuevoCosto[p.producto_id || p.nombre]);
    if (!Number.isFinite(nc)) return null;
    return { nuevo: nc, diff: nc - (p.costo_actual || 0), pct: p.costo_actual > 0 ? ((nc - p.costo_actual) / p.costo_actual * 100) : null };
  };

  const guardarCosto = async (p) => {
    if (!p.producto_id) return toast.error("Este producto no está vinculado al catálogo (no tiene id)");
    const key = p.producto_id;
    const nc = parseFloat(nuevoCosto[key]);
    if (!Number.isFinite(nc) || nc < 0) return toast.warning("Ingresá un costo válido");
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.put(`/productos/${p.producto_id}`, { ultimo_costo: nc });
      toast.success(`Costo actualizado a ${fmtMoney(nc)} · ${p.nombre}`);
      setNuevoCosto(s => { const c = { ...s }; delete c[key]; return c; });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  const exportarCsv = () => {
    const rows = [
      ["Código", "Producto", "Kg vendidos", "Facturado", "Precio prom/kg", "Costo actual", "Costo total valorizado", "Margen %"],
      ...filtered.map(p => [
        p.codigo || "", p.nombre, p.kilos_vendidos, p.facturado,
        p.precio_prom_kg, p.costo_actual, p.costo_total_valorizado,
        p.margen_actual_pct ?? "",
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `productos_vendidos_${data?.desde}_${data?.hasta}.csv`; a.click();
  };

  const totalKg = filtered.reduce((s, p) => s + p.kilos_vendidos, 0);
  const totalFacturado = filtered.reduce((s, p) => s + p.facturado, 0);
  const totalCostoActual = filtered.reduce((s, p) => s + p.costo_total_valorizado, 0);

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="dash-productos-vendidos">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <Package size={16} weight="bold" className="text-[#6B1F24]" />
            Productos Vendidos · Actualizar Costos
          </h2>
          <div className="text-[11px] text-[#666] font-mono mt-0.5">
            Kilos y facturación por producto · costo actual del sistema · calculá y aplicá nuevos costos
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar producto…" value={q} onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 w-56 text-[12px]"
              data-testid="dash-prod-vendidos-search"
            />
          </div>
          <Button variant="outline" onClick={exportarCsv} className="h-8 rounded-sm text-[12px]" disabled={filtered.length === 0}
                  data-testid="dash-prod-vendidos-export">
            <DownloadSimple size={13} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {loading && (
        <div className="p-8 text-center text-neutral-500 text-sm">Cargando…</div>
      )}
      {!loading && totalAlertas > 0 && (
        <div className="px-5 py-2 border-b border-rose-200 bg-rose-50 flex items-center gap-3 flex-wrap"
             data-testid="alerta-precios-bajos">
          <Warning size={16} weight="fill" className="text-rose-700" />
          <div className="text-[12px] text-rose-800 flex-1">
            <span className="font-bold">{totalAlertas}</span> venta{totalAlertas === 1 ? "" : "s"} en{" "}
            <span className="font-bold">{productosConAlerta}</span> producto{productosConAlerta === 1 ? "" : "s"} con precio{" "}
            <span className="font-bold">10 %+ por debajo</span> del precio de lista más bajo.
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSoloAlertas(v => !v)}
            className={`h-7 rounded-sm text-[11px] ${soloAlertas ? "bg-rose-700 text-white border-rose-700 hover:bg-rose-800" : "border-rose-300 text-rose-800 hover:bg-rose-100"}`}
            data-testid="alerta-precios-toggle"
          >
            {soloAlertas ? "Ver todos" : "Ver sólo alertas"}
          </Button>
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="p-8 text-center text-neutral-400 text-sm">
          Sin productos vendidos en el período seleccionado.
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Producto</th>
                  <th className="px-3 py-2 text-right w-24">Kg vendidos</th>
                  <th className="px-3 py-2 text-right w-28">Facturado</th>
                  <th className="px-3 py-2 text-right w-28">Precio prom/kg</th>
                  <th className="px-3 py-2 text-right w-28">Costo actual</th>
                  <th className="px-3 py-2 text-right w-32">Costo × Kg</th>
                  <th className="px-3 py-2 text-right w-28">Nuevo costo</th>
                  <th className="px-3 py-2 text-right w-28">Diferencia</th>
                  <th className="px-3 py-2 text-center w-16">Acción</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((p, i) => {
                  const key = p.producto_id || p.nombre;
                  const diff = calcDiff(p);
                  const isSaving = !!saving[key];
                  return (
                    <tr key={key} className="border-t border-neutral-100 hover:bg-neutral-50/60"
                        data-testid={`dash-prod-vendidos-row-${i}`}>
                      <td className="px-3 py-1.5">
                        <div className="font-medium truncate max-w-[240px] flex items-center gap-1.5" title={p.nombre}>
                          {p.nombre}
                          {(p.cant_alertas || 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => setAlertasDetalle(p)}
                              className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase bg-rose-100 text-rose-800 border border-rose-300 px-1 py-0.5 rounded-sm hover:bg-rose-200"
                              title={`${p.cant_alertas} venta(s) a precio 10%+ debajo del mínimo de lista (${fmtMoney(p.precio_lista_min)})`}
                              data-testid={`alerta-precio-badge-${i}`}
                            >
                              <Warning size={9} weight="fill" /> {p.cant_alertas}
                            </button>
                          )}
                        </div>
                        {p.codigo && <div className="text-[10px] font-mono text-neutral-400">{p.codigo}</div>}
                        {!p.vinculado && <div className="text-[10px] text-rose-600 font-mono">Sin vínculo al catálogo</div>}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtKg(p.kilos_vendidos).replace(" kg","")}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(p.facturado)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">
                        <button
                          type="button"
                          onClick={() => setDesglose(p)}
                          className="text-sky-700 hover:text-sky-900 hover:underline font-mono cursor-pointer transition-colors"
                          title="Ver desglose por precio · click para abrir"
                          data-testid={`dash-prod-vendidos-precio-${i}`}
                        >
                          {fmtMoney(p.precio_prom_kg)}
                        </button>
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">
                        <span className="font-bold text-neutral-800">{fmtMoney(p.costo_actual)}</span>
                        {p.margen_actual_pct != null && (
                          <div className={`text-[10px] font-mono ${p.margen_actual_pct >= 30 ? "text-emerald-700" : p.margen_actual_pct >= 15 ? "text-amber-700" : "text-rose-700"}`}>
                            Margen {p.margen_actual_pct.toFixed(1)}%
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-neutral-700">{fmtMoney(p.costo_total_valorizado)}</td>
                      <td className="px-3 py-1.5">
                        <NumberInput step="0.01" min="0"
                          value={nuevoCosto[key] ?? ""}
                          onChange={(e) => setNuevoCosto(s => ({ ...s, [key]: e.target.value }))}
                          placeholder={String(p.costo_actual)}
                          className="h-7 w-full text-[11px] font-mono text-right"
                          disabled={!p.vinculado}
                          data-testid={`dash-prod-vendidos-input-${i}`}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">
                        {diff ? (
                          <div>
                            <div className={diff.diff > 0 ? "text-rose-700 font-bold" : diff.diff < 0 ? "text-emerald-700 font-bold" : "text-neutral-500"}>
                              {diff.diff > 0 ? "+" : ""}{fmtMoney(diff.diff)}
                            </div>
                            {diff.pct != null && (
                              <div className={`text-[10px] flex items-center justify-end gap-0.5 ${diff.diff > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                                {diff.diff > 0 ? <TrendUp size={9} weight="bold"/> : <TrendDown size={9} weight="bold"/>}
                                {diff.pct > 0 ? "+" : ""}{diff.pct.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => guardarCosto(p)}
                          disabled={!diff || isSaving || !p.vinculado}
                          className="h-7 px-2 text-emerald-700 hover:bg-emerald-50 disabled:opacity-30"
                          title={!p.vinculado ? "Producto sin id de catálogo" : "Guardar nuevo costo"}
                          data-testid={`dash-prod-vendidos-save-${i}`}
                        >
                          <FloppyDisk size={13} weight="bold" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-neutral-50 font-mono font-bold text-[11px] border-t-2 border-neutral-300">
                <tr>
                  <td className="px-3 py-2 uppercase text-neutral-700">Total ({filtered.length})</td>
                  <td className="px-3 py-2 text-right">{fmtKg(totalKg).replace(" kg","")}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(totalFacturado)}</td>
                  <td className="px-3 py-2 text-right text-neutral-400">—</td>
                  <td className="px-3 py-2 text-right text-neutral-400">—</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(totalCostoActual)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {filtered.length > 10 && (
            <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="dash-prod-vendidos-paginacion">
              <div className="text-[11px] font-mono text-neutral-600">
                Mostrando <span className="font-bold text-neutral-800">{visibleRows.length}</span> de{" "}
                <span className="font-bold text-neutral-800">{filtered.length}</span> productos
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase font-mono text-neutral-500">Ver</label>
                <select
                  value={visibleCount >= filtered.length ? "all" : String(visibleCount)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVisibleCount(v === "all" ? filtered.length : parseInt(v, 10));
                  }}
                  className="h-8 text-[12px] font-mono border border-neutral-300 rounded-sm px-2 bg-white focus:outline-none focus:border-[#6B1F24]"
                  data-testid="dash-prod-vendidos-page-size"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">Todos</option>
                </select>
                {visibleCount < filtered.length && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setVisibleCount((v) => Math.min(filtered.length, v + 10))}
                    className="h-8 rounded-sm text-[11px]"
                    data-testid="dash-prod-vendidos-ver-mas"
                  >
                    Ver 10 más
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {desglose && (
        <DesglosePrecioModal
          producto={desglose}
          fechaDesde={fechaDesde}
          fechaHasta={fechaHasta}
          onClose={() => setDesglose(null)}
        />
      )}
      {alertasDetalle && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAlertasDetalle(null)}
             data-testid="modal-alertas-precio">
          <div className="bg-white rounded-sm border border-rose-300 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-rose-200 bg-rose-50 flex items-center justify-between">
              <div>
                <div className="font-heading font-semibold text-[15px] text-rose-900 flex items-center gap-2">
                  <Warning size={16} weight="fill" className="text-rose-700" />
                  Ventas con precio bajo — {alertasDetalle.nombre}
                </div>
                <div className="text-[11px] text-rose-700 font-mono mt-0.5">
                  Precio de lista más bajo: {fmtMoney(alertasDetalle.precio_lista_min)} · Umbral alerta: −10 %
                </div>
              </div>
              <button onClick={() => setAlertasDetalle(null)} className="text-neutral-500 hover:text-neutral-800">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-[12px]">
                <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600">
                  <tr>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Factura</th>
                    <th className="px-4 py-2 text-left">Cliente</th>
                    <th className="px-4 py-2 text-right">Kg</th>
                    <th className="px-4 py-2 text-right">Precio facturado</th>
                    <th className="px-4 py-2 text-right">vs Lista mín.</th>
                  </tr>
                </thead>
                <tbody>
                  {alertasDetalle.alertas_precio_bajo.map((a, i) => (
                    <tr key={i} className="border-t border-neutral-100 hover:bg-rose-50/40"
                        data-testid={`modal-alertas-row-${i}`}>
                      <td className="px-4 py-1.5 font-mono text-neutral-500">{a.fecha}</td>
                      <td className="px-4 py-1.5 font-mono font-bold">{a.factura_numero}</td>
                      <td className="px-4 py-1.5 truncate max-w-[180px]" title={a.cliente}>{a.cliente || "—"}</td>
                      <td className="px-4 py-1.5 text-right font-mono">{fmtKg(a.kilos).replace(" kg", "")}</td>
                      <td className="px-4 py-1.5 text-right font-mono font-bold text-rose-700">{fmtMoney(a.precio_facturado)}</td>
                      <td className="px-4 py-1.5 text-right font-mono text-rose-700">
                        {a.diferencia_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
